import React from 'react';
import { toast } from 'sonner';
import { CalendarClock, ChevronDown, Loader2, RefreshCw } from 'lucide-react';
import { adminApi } from '../../../lib/adminAuth';
import RetainerGapRow from './RetainerGapRow';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

export default function RetainerGapPanel({ onOpenInvoice, refreshKey, onSummary }) {
  const [month, setMonth] = React.useState(thisMonth());
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState([]);
  const [busyIds, setBusyIds] = React.useState([]);
  const [showOther, setShowOther] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/api/admin/accounting/retainer-gaps', { params: month === thisMonth() ? {} : { month } });
      setData(r.data);
      onSummary?.(r.data.summary);
      setSelected((s) => s.filter((id) => r.data.rows.some((x) => x.client_id === id && ['gap', 'due'].includes(x.status))));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load retainer gaps'); }
    finally { setLoading(false); }
  }, [month, onSummary]);
  React.useEffect(() => { load(); }, [load, refreshKey]);

  const create = async (ids) => {
    setBusyIds(ids);
    try {
      const r = await adminApi.post('/api/admin/accounting/retainer-gaps/drafts', { client_ids: ids, month: month === thisMonth() ? null : month });
      const { created = [], skipped = [] } = r.data;
      if (created.length) toast.success(`${created.length} draft invoice${created.length > 1 ? 's' : ''} created`);
      skipped.forEach((s) => toast.warning(`${s.client_name || 'Client'}: ${s.reason}`));
      setSelected([]);
      await load();
      if (created.length === 1) onOpenInvoice?.(created[0]);
      else if (created.length > 1) onOpenInvoice?.(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create drafts'); }
    finally { setBusyIds([]); }
  };

  const openExisting = async (id) => {
    try { const r = await adminApi.get(`/api/admin/accounting/retainer-gaps/invoice/${id}`); onOpenInvoice?.(r.data); }
    catch { toast.error('Could not open invoice'); }
  };

  const rows = data?.rows || [];
  const main = rows.filter((r) => ['gap', 'due', 'draft', 'invoiced'].includes(r.status));
  const other = rows.filter((r) => !['gap', 'due', 'draft', 'invoiced'].includes(r.status));
  const actionable = main.filter((r) => ['gap', 'due'].includes(r.status)).map((r) => r.client_id);
  const s = data?.summary || {};
  const toggle = (id) => setSelected((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
  const rowProps = (r) => ({ row: r, checked: selected.includes(r.client_id), onCheck: toggle, onCreate: create, onOpen: openExisting, busy: busyIds.includes(r.client_id) });

  return (
    <div className="soft-card p-4 sm:p-5" data-testid="retainer-gap-panel">
      <div className="flex flex-wrap items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-fuchsia-100 flex items-center justify-center shrink-0"><CalendarClock className="w-4 h-4 text-fuchsia-700" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#1a1a2e] flex items-center gap-2">Retainer gaps {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-fuchsia-600" />}</h3>
          <p className="text-xs text-slate-500" data-testid="retainer-gap-summary">
            {data ? `${s.gap || 0} not invoiced · ${s.due || 0} due · ${s.draft || 0} drafts · ${s.invoiced || 0} invoiced — ${inr(s.open_amount)} still to bill for ${data.month_label}` : 'Checking retainer clients…'}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">&ldquo;Due&rdquo; during the first {data?.grace_days ?? 5} days of a billing period, then &ldquo;Gap&rdquo;. Quarterly and yearly retainers follow the April–March financial year.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={month} max={thisMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white" data-testid="retainer-gap-month" />
          <button type="button" onClick={load} className="p-1.5 rounded-lg hover:bg-slate-100" title="Refresh" data-testid="retainer-gap-refresh"><RefreshCw className="w-4 h-4 text-slate-600" /></button>
        </div>
      </div>

      {actionable.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
          <label className="inline-flex items-center gap-2 font-semibold text-slate-700 cursor-pointer">
            <input type="checkbox" checked={selected.length === actionable.length} onChange={(e) => setSelected(e.target.checked ? actionable : [])}
              className="w-4 h-4 accent-fuchsia-600" data-testid="retainer-gap-select-all" /> Select all not invoiced
          </label>
          {selected.length > 0 && (
            <button type="button" onClick={() => create(selected)} disabled={busyIds.length > 0}
              className="ml-auto h-8 px-3 rounded-full bg-[#1a1a2e] hover:bg-fuchsia-700 text-white font-semibold active:scale-95 transition disabled:opacity-60"
              data-testid="retainer-gap-bulk-create">Create {selected.length} draft{selected.length > 1 ? 's' : ''}</button>
          )}
        </div>
      )}

      {main.length === 0 && !loading ? (
        <p className="text-sm text-slate-500 text-center py-6" data-testid="retainer-gap-empty">No active retainer clients for this period</p>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200/70 rounded-xl overflow-hidden" data-testid="retainer-gap-list">
          {main.map((r) => <RetainerGapRow key={r.client_id} {...rowProps(r)} />)}
        </ul>
      )}

      {other.length > 0 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowOther((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-fuchsia-700" data-testid="retainer-gap-toggle-other">
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showOther ? 'rotate-180' : ''}`} />
            {other.length} other ({s.lapsed || 0} possibly stopped, {s.ended || 0} ended)
          </button>
          {showOther && (
            <ul className="mt-2 divide-y divide-slate-100 border border-slate-200/70 rounded-xl overflow-hidden" data-testid="retainer-gap-other-list">
              {other.map((r) => <RetainerGapRow key={r.client_id} {...rowProps(r)} />)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
